import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { USE_CASES } from "../../data/useCases";
import { useDocMeta } from "../../lib/useDocMeta";
import { track, EVENT } from "../../lib/publicAnalytics";

export default function UseCases() {
  useDocMeta({
    title: "Use Cases · GovBidIQ",
    description: "How GovBidIQ helps SDVOSB, 8(a), WOSB, HUBZone, primes, subs, consultants, and capture / proposal teams.",
    path: "/use-cases",
  });

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-14">
      <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3">/ Use Cases</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl text-slate-100 leading-tight mb-3">Built for every kind of federal contractor.</h1>
      <p className="text-slate-400 text-lg max-w-2xl leading-relaxed mb-10">
        Eleven personas, one platform. Pick the one that fits your business and see the exact features and next step we recommend.
      </p>

      <div className="grid lg:grid-cols-2 gap-px bg-[var(--border-soft)]">
        {USE_CASES.map((u) => (
          <article
            key={u.slug}
            className="bg-[var(--bg-secondary)] p-6 flex flex-col"
            data-testid={`use-case-${u.slug}`}
            onMouseEnter={() => track(EVENT.USE_CASE_VIEWED, { slug: u.slug })}
          >
            <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-1.5">/ {u.label}</div>
            <h2 className="font-display font-bold text-slate-100 text-xl mb-1.5">{u.persona}</h2>

            <div className="mt-3 space-y-3 text-sm leading-relaxed">
              <div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-1">Common challenge</div>
                <p className="text-slate-300">{u.challenge}</p>
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-1">How GovBidIQ helps</div>
                <p className="text-slate-300">{u.how_we_help}</p>
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-1">Best-fit features</div>
                <ul className="space-y-1">
                  {u.best_fit_features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-slate-200">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-[var(--border-soft)] flex items-center justify-between gap-3">
              <span className="text-xs text-slate-400 flex-1">Next step · {u.next_step}</span>
              <Link to="/tour" className="inline-flex items-center gap-1 font-mono text-[10px] tracking-widest uppercase text-sky-400 hover:text-sky-300 shrink-0" data-testid={`use-case-cta-${u.slug}`}>
                Tour <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
